//@flow
import {useCallback, useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {actions} from '../duck';
import {useActions} from '../../../../utils/globalStore';
import {useSelector} from 'react-redux';
import {shallowEqual} from 'react-redux';

import {
  setTagToConversationService,
  changeConversationStateService,
} from '../promises/conversationTag';
import {
  getConversationCountService,
  getConversationsService,
  getChatbotByIdService,
  editChatbotService,
} from '../promises';
import {formatPhoneNumber} from '../../../../utils/phone';
import {
  setConversationCollaboratorService,
  assignMeConversationService,
  clearAssignmentConversationService,
} from '../promises/conversationCollaborator';

import type {RootState} from '../../..';
import type {
  Conversation,
  Pagination,
  MessageScheduleState,
  ChatBot,
} from '../../../../entities';
import {useUserType} from '../../../auth/utils';
import {
  updateConversationsService,
  updateConversationChatbotService,
  updateConversationService,
} from '../promises/conversation';
import {addConversationToFunnelService} from '../../funnels/promises';

export const MAX_IDENTIFIER_AND_PHONE_VALID_LENGTH = 13;

/** Temporary: WhatsApp username IDs are longer than real phones; hide them as unknown. */
export function isValidDisplayIdentifierOrPhone(value: ?string): boolean {
  if (!value) return false;
  return value.trim().length <= MAX_IDENTIFIER_AND_PHONE_VALID_LENGTH;
}

export function getConversationDisplayName(conversation: Conversation) {
  if (!conversation.contactInformation) return 'Usuario desconocido';

  const {
    firstName,
    fullName,
    phone,
    pushName,
    name,
    username,
    contactName,
  } = conversation.contactInformation;

  if (contactName) return contactName;
  if (name) return name;
  if (username) return username;
  if (fullName) return fullName;
  if (pushName) return pushName;
  if (firstName) return firstName;
  if (isValidDisplayIdentifierOrPhone(phone))
    return formatPhoneNumber(`+${phone}`);

  const identifier = conversation.identifier?.trim() ?? '';
  if (isValidDisplayIdentifierOrPhone(identifier)) return identifier;

  return 'Usuario desconocido';
}

const setConversationActions = {
  setStoreConversations: actions.setStoreConversations,
  setCurrentConversation: actions.setCurrentConversation,
};

const conversationPaginationAction = actions.addConversationPagination;

const conversationPaginationSelector = (state: RootState) =>
  state.chat.conversationPagination;

export function useSetInitialWhatsappConversations() {
  const conversationPagination = useSelector(conversationPaginationSelector);
  const addConversationPagination = useActions(conversationPaginationAction);

  const [loading, setLoading] = useState(false);

  const {
    state,
    admin,
    collaborators,
    identifier,
    pagination,
    tags,
    services,
    requiresAssistance,
    scheduleMessageStates,
    deviceId,
  } = useFilterConversations();

  const {setStoreConversations, setCurrentConversation} = useActions(
    setConversationActions
  );

  const fetchInitialConversations = useCallback(() => {
    setLoading(true);

    getConversationsService(services, {
      state,
      admin,
      collaborators,
      identifier,
      pagination,
      tags,
      requiresAssistance,
      scheduleMessageState: scheduleMessageStates?.join(','),
      deviceId,
    })
      .then(res => {
        // if (res.data.length === 0) return;
        setStoreConversations(res.data, res.pagination);
        setCurrentConversation(res.data[0]);
      })
      .catch(e => {
        console.log('Error getting conversations', e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    services,
    state,
    admin,
    collaborators,
    identifier,
    pagination,
    tags,
    requiresAssistance,
    scheduleMessageStates,
    deviceId,
    setStoreConversations,
    setCurrentConversation,
  ]);

  useEffect(() => {
    fetchInitialConversations();
  }, [fetchInitialConversations]);

  const addPagination = useCallback(() => {
    if (!conversationPagination || loading) return;

    const pagination: Pagination = {
      ...conversationPagination,
      page: conversationPagination.page + 1,
    };

    const currentConversations = pagination.page * pagination.size;
    const hasMore = currentConversations < pagination.total;

    if (!hasMore) return;

    setLoading(true);
    getConversationsService(services, {
      state,
      admin,
      collaborators,
      identifier,
      tags,
      pagination,
      requiresAssistance,
      deviceId,
    })
      .then(res => {
        addConversationPagination(res.data, res.pagination);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    addConversationPagination,
    admin,
    collaborators,
    conversationPagination,
    identifier,
    loading,
    requiresAssistance,
    services,
    state,
    tags,
    deviceId,
  ]);

  return {
    loading,
    addPagination,
    setStoreConversations,
    refreshConversations: fetchInitialConversations,
  };
}

const filterConversationsActions = {
  setStateFilter: actions.setStateConversationFilter,
  setPagination: actions.setPaginationConversationFilter,
  setTagsFilter: actions.setTagsConversationFilter,
  setCollaboratorsFilter: actions.setCollaboratorsConversationFilter,
  setIdentifierFilter: actions.setIdentifierConversationFilter,
  setAdminFilter: actions.setAdminConversationFilter,
  setScheduleMessageStateFilter:
    actions.setScheduleMessageStateConversationFilter,
  setServicesFilter: actions.setServicesConversationFilter,
  setDeviceIdFilter: actions.setDeviceIdConversationFilter,
  setRequiresAssistanceFilter: actions.setRequiresAssistanceConversationFilter,
  clearFiltersConversation: actions.clearFilterConversation,
};

export function useFilterConversationsActions() {
  const {
    setStateFilter,
    setPagination,
    setTagsFilter,
    setCollaboratorsFilter,
    setIdentifierFilter,
    setAdminFilter,
    clearFiltersConversation,
    setScheduleMessageStateFilter,
    setServicesFilter,
    setDeviceIdFilter,
    setRequiresAssistanceFilter,
  } = useActions(filterConversationsActions);

  const filterByTags = useCallback(
    (tags: string[]) => {
      setTagsFilter(tags);
    },
    [setTagsFilter]
  );

  const filterByState = useCallback(
    (state?: string) => {
      setStateFilter(state);
    },
    [setStateFilter]
  );

  const filterByPagination = useCallback(
    (pagination: Pagination) => {
      setPagination(pagination);
    },
    [setPagination]
  );

  const filterByIdentifier = useCallback(
    (identifier?: string) => {
      setIdentifierFilter(identifier);
    },
    [setIdentifierFilter]
  );

  const filterByCollaborator = useCallback(
    (collaborators: string[]) => {
      setCollaboratorsFilter(collaborators);
    },
    [setCollaboratorsFilter]
  );

  const filterByAdmin = useCallback(
    (admin: boolean) => {
      setAdminFilter(admin);
    },
    [setAdminFilter]
  );

  const filterByRequiresAssistance = useCallback(
    (requiresAssistance: boolean) => {
      setRequiresAssistanceFilter(requiresAssistance);
    },
    [setRequiresAssistanceFilter]
  );

  const filterByScheduleMessageState = useCallback(
    (scheduleMessageStates?: MessageScheduleState[]) => {
      setScheduleMessageStateFilter(scheduleMessageStates);
    },
    [setScheduleMessageStateFilter]
  );

  const filterByServices = useCallback(
    (services: string[], deviceId?: ?string) => {
      setServicesFilter(services, deviceId);
    },
    [setServicesFilter]
  );

  const filterByDeviceId = useCallback(
    (deviceId?: ?string) => {
      setDeviceIdFilter(deviceId);
    },
    [setDeviceIdFilter]
  );

  const clearFilters = useCallback(() => {
    clearFiltersConversation();
  }, [clearFiltersConversation]);

  return {
    filterByTags,
    filterByState,
    filterByPagination,
    filterByIdentifier,
    filterByCollaborator,
    filterByAdmin,
    clearFilters,
    filterByServices,
    filterByDeviceId,
    filterByRequiresAssistance,
    filterByScheduleMessageState,
  };
}

const filterConversationSelector = (state: RootState) =>
  state.chat.conversationFilters;

export function useFilterConversations() {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const searchValueFromParams = searchParams.get('identifier') || '';

  const filter = useSelector(filterConversationSelector, shallowEqual);

  return {
    ...filter,
    identifier:
      typeof filter.identifier === 'string'
        ? filter.identifier
        : searchValueFromParams,
  };
}

export function useConversations(): Conversation[] {
  const conversations = useSelector(
    (state: RootState) => state.chat.conversations
  );

  return conversations;
}

const conversationsActions = {
  setUnreadConversation: actions.setUnreadConversation,
};

export function useConversationsActions() {
  const {setUnreadConversation} = useActions(conversationsActions);

  const markAsRead = useCallback(
    (conversationId: string) => {
      setUnreadConversation(conversationId, false);
    },
    [setUnreadConversation]
  );

  return {
    markAsRead,
  };
}

const currentConversationSelector = (state: RootState) =>
  state.chat.currentConversation;

const currentConversationActions = {
  setCurrentConversation: actions.setCurrentConversation,
  clearCurrentConversation: actions.clearCurrentConversation,
  getStoreDevices: actions.getStoreDevices,
};

export function useCurrentConversation(): {
  currentConversation: Conversation | typeof undefined,
  setCurrentConversation: (conversation: Conversation) => void,
  clearCurrentConversation: () => void,
} {
  const currentConversation = useSelector(currentConversationSelector);

  const actions = useActions(currentConversationActions);

  const setCurrentConversation = useCallback(
    (conversation: Conversation) => {
      actions.getStoreDevices([
        'WHATSAPP_WEB',
        'FB_MESSENGER',
        'INSTAGRAM',
        'WHATSAPP_API',
      ]);
      actions.setCurrentConversation(conversation);
    },
    [actions]
  );

  return {
    currentConversation,
    setCurrentConversation,
    clearCurrentConversation: actions.clearCurrentConversation,
  };
}

export function useSetTagToConversation(conversationId: string) {
  const [loading, setLoading] = useState(false);
  const updateStoreConversationTags = useActions(
    actions.updateStoreConversationTags
  );

  const setTagToConversation = useCallback(
    async (crmTagIds: string[]) => {
      setLoading(true);
      try {
        const conversation = await setTagToConversationService(
          conversationId,
          crmTagIds
        );

        updateStoreConversationTags(conversation.id, conversation.tags);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [conversationId, updateStoreConversationTags]
  );

  return {
    loading,
    setTagToConversation,
  };
}

export function useCurrentConversationState() {
  const {currentConversation} = useCurrentConversation();
  const updateConversation = useActions(actions.updateStoreConversation);
  const {setAdminCount, setCollaboratorCount, setTotalCount} = useActions(
    conversationCountActions
  );

  const {state} = useFilterConversations();

  const {
    admin: adminCount,
    collaborators: collaboratorsCount,
    total: totalCount,
  } = useConversationCount();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => {
      setError(false);
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const toggleConversationState = useCallback(async () => {
    if (!currentConversation) return;
    const {id, crmConversationState, userType, userId} = currentConversation;

    const newState = crmConversationState === 'OPEN' ? 'CLOSED' : 'OPEN';
    setLoading(true);
    changeConversationStateService(id, newState)
      .then(() => {
        updateConversation({
          id,
          crmConversationState: newState,
        });

        if (state === 'ALL') return;

        if (newState !== state) {
          setTotalCount(totalCount - 1);
          if (userType === 'VENDOR') setAdminCount(adminCount - 1);
          if (userType === 'COLLABORATOR' && userId) {
            const collaboratorId = `C${userId}`;
            const collaborator = collaboratorsCount.find(
              collaborator => collaborator.id === collaboratorId
            );
            if (collaborator)
              setCollaboratorCount(collaboratorId, collaborator.count - 1);
          }
        }

        if (newState === state) {
          setTotalCount(totalCount + 1);
          if (userType === 'VENDOR') setAdminCount(adminCount + 1);
          if (userType === 'COLLABORATOR' && userId) {
            const collaboratorId = `C${userId}`;
            const collaborator = collaboratorsCount.find(
              collaborator => collaborator.id === collaboratorId
            );
            if (collaborator)
              setCollaboratorCount(collaboratorId, collaborator.count + 1);
          }
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    adminCount,
    collaboratorsCount,
    currentConversation,
    setAdminCount,
    setCollaboratorCount,
    setTotalCount,
    state,
    totalCount,
    updateConversation,
  ]);

  return {
    error,
    loading,
    toggleConversationState,
    state: currentConversation && currentConversation.crmConversationState,
  };
}

const conversationCountActions = {
  setAdminCount: actions.setAdminConversationCount,
  setTotalCount: actions.setTotalConversationCount,
  setCollaboratorCount: actions.setCollaboratorConversationCount,
  setConversationCount: actions.setConversationCount,
  updateCurrentConversation: actions.updateCurrentConversation,
  updateConversation: actions.updateStoreConversation,
  toggleRequiredAssistanceAction: actions.toggleRequiredAssistance,
};

export function useCurrentConversationActions() {
  const {currentConversation} = useCurrentConversation();

  const {
    admin: adminCount,
    collaborators: collaboratorsCount,
  } = useConversationCount();

  const {userId} = useUserType();

  const {
    setAdminCount,
    setCollaboratorCount,
    updateCurrentConversation,
    updateConversation,
  } = useActions(conversationCountActions);

  const clearAssignment = () => {
    if (!currentConversation) return;

    const {userId, userType} = currentConversation;

    clearAssignmentConversationService(currentConversation.id).then(
      conversation => {
        const collaborator = collaboratorsCount.find(
          collaborator => collaborator.id === userId
        );

        if (userType === 'VENDOR') {
          setAdminCount(adminCount - 1);
        }

        setCollaboratorCount(userId, collaborator ? collaborator.count - 1 : 0);

        updateCurrentConversation(conversation);
      }
    );
  };

  const assignCollaborator = (collaboratorId: string) => {
    if (!currentConversation) return;

    const {userType} = currentConversation;
    setConversationCollaboratorService(
      currentConversation.id,
      collaboratorId
    ).then(conversation => {
      const collaborator = collaboratorsCount.find(
        collaborator => collaborator.id === collaboratorId
      );

      if (userType === 'VENDOR') {
        setAdminCount(adminCount - 1);
      }

      setCollaboratorCount(
        collaboratorId,
        collaborator ? collaborator.count + 1 : 0
      );

      updateCurrentConversation(conversation);
    });
  };

  const assignToMe = () => {
    if (!currentConversation) return;

    assignMeConversationService(currentConversation.id, userId).then(
      conversation => {
        const {userId, userType} = currentConversation;
        if (userType === 'COLLABORATOR' && userId) {
          const collaboratorId = `C${userId}`;
          const collaborator = collaboratorsCount.find(
            collaborator => collaborator.id === collaboratorId
          );

          setCollaboratorCount(
            userId,
            collaborator ? collaborator.count - 1 : 0
          );
        }

        setAdminCount(adminCount + 1);
        updateCurrentConversation(conversation);
      }
    );
  };

  const markAsUnread = () => {
    if (!currentConversation) return;

    return updateConversationsService({
      crmConversationIds: [currentConversation.id],
      read: false,
    }).then(() => {
      const newConversation = {
        ...currentConversation,
        read: false,
      };
      updateConversation(newConversation);

      return newConversation;
    });
  };

  const setConversationFunnel = (
    funnelId: string,
    funnelName: string,
    laneId: string
  ) => {
    if (!currentConversation) return;

    const {id} = currentConversation;

    return addConversationToFunnelService(funnelId, laneId, [id]).then(() => {
      const newConversation = {
        ...currentConversation,
        funnel: {
          name: funnelName,
          id: funnelId,
        },
      };
      updateConversation(newConversation);

      return newConversation;
    });
  };

  const toggleRequiredAssistance = () => {
    if (!currentConversation) return;

    const {id, requiresAssistance} = currentConversation;

    // toggleRequiredAssistanceAction(id, !requiresAssistance);

    return updateConversationsService({
      crmConversationIds: [id],
      requiresAssistance: !requiresAssistance,
    }).then(() => {
      const newConversation = {
        ...currentConversation,
        requiresAssistance: !requiresAssistance,
      };
      updateConversation(newConversation);
    });
  };

  return {
    setConversationFunnel,
    assignCollaborator,
    clearAssignment,
    markAsUnread,
    assignToMe,
    toggleRequiredAssistance,
  };
}

const conversationCountSelector = (state: RootState) =>
  state.chat.conversationCount;

export function useConversationCount() {
  const conversationCount = useSelector(conversationCountSelector);

  return conversationCount;
}

export function useInitializeConversationCount() {
  const conversationCount = useSelector(conversationCountSelector);

  const {setConversationCount} = useActions(conversationCountActions);

  const {state, services} = useFilterConversations();

  useEffect(() => {
    const initializeCounters = async () => {
      const conversationCount = await getConversationCountService({
        services,
        state,
      });

      setConversationCount({
        total: conversationCount.totalCount,
        admin: conversationCount.personalCount,
        requiresAssistanceCount: conversationCount.requiresAssistanceCount ?? 0,
        collaborators: conversationCount.collaboratorCounts.map(
          ({collaboratorId, count}) => ({
            id: collaboratorId,
            count,
          })
        ),
      });
    };

    initializeCounters().catch(error => {
      console.log(error);
    });
  }, [services, setConversationCount, state]);

  /*   useEffect(() => {
    if (!collaborators || collaborators.length === 0) return;

    const promises = collaborators.map(collaborator =>
      getConversationCountService({
        services: ['WHATSAPP_WEB', 'INSTAGRAM', 'FB_MESSENGER'],
        collaborators: [collaborator.id],
        state,
      }).then(({count}) => {
        return {
          id: collaborator.id,
          count,
        };
      })
    );

    Promise.all(promises).then(results => {
      results.forEach(({id, count}) => {
        setCollaboratorCount(id, count);
      });
    });
  }, [collaborators, setCollaboratorCount, state]); */

  return conversationCount;
}

const conversationsSelectedActions = {
  toggleConversationSelectedAction: actions.togglerConversationSelection,
  clearSelectedConversations: actions.clearConversationSelection,
  setMultipleConversationState: actions.setMultipleConversationState,
  setUnreadMultipleConversation: actions.setUnreadMultipleConversation,
};

export function useConversationSelectedActions() {
  const conversationSelected = useSelector(conversationSelectedSelector);
  const {
    toggleConversationSelectedAction,
    setMultipleConversationState,
    clearSelectedConversations,
    setUnreadMultipleConversation,
  } = useActions(conversationsSelectedActions);

  const toggleConversationSelected = useCallback(
    (conversation: Conversation) => {
      toggleConversationSelectedAction(conversation);
    },
    [toggleConversationSelectedAction]
  );

  const closeSelectedConversations = () => {
    const conversationsIds = conversationSelected.map(
      conversation => conversation.id
    );

    updateConversationsService({
      crmConversationIds: conversationsIds,
      state: 'CLOSED',
    })
      .then(() => {
        setMultipleConversationState(conversationsIds, 'CLOSED');

        clearSelectedConversations();
      })
      .catch(() => {
        console.log('Error closing conversations');
      });
  };

  const markAsUnreadSelectedConversations = () => {
    const conversationsIds = conversationSelected.map(
      conversation => conversation.id
    );

    const unread = true;
    updateConversationsService({
      crmConversationIds: conversationsIds,
      read: !unread,
    })
      .then(() => {
        setUnreadMultipleConversation(conversationsIds, unread);

        clearSelectedConversations();
      })
      .catch(() => {
        console.log('Error marking as unread conversations');
      });
  };

  return {
    toggleConversationSelected,
    markAsUnreadSelectedConversations,
    closeSelectedConversations,
  };
}

const conversationSelectedSelector = (state: RootState) =>
  state.chat.selectedConversations;

export function useConversationSelected() {
  const selectedConversations = useSelector(conversationSelectedSelector);

  return selectedConversations;
}

const isSynchronizingConversationsSelector = (state: RootState) =>
  state.chat.isSynchronizingConversations;

export function useConversationsSynchronizing() {
  const isSynchronizingConversations = useSelector(
    isSynchronizingConversationsSelector
  );

  const updateConversationsSynchronizingAction = useActions(
    actions.setConversationSyncronizing
  );

  const updateConversationSynchronizing = useCallback(
    (isSynchronizing: boolean) => {
      updateConversationsSynchronizingAction(isSynchronizing);
    },
    [updateConversationsSynchronizingAction]
  );

  return {isSynchronizingConversations, updateConversationSynchronizing};
}

const conversationIdSelector = (state: RootState) => state.chat.conversationId;

export function useConversationIdSelected() {
  const conversationId = useSelector(conversationIdSelector);

  const setConversationIdActions = useActions(actions.setIdConversation);
  const resetConversationId = useActions(actions.resetIdConversation);

  const setConversationId = useCallback(
    (conversationId: string) => {
      setConversationIdActions(conversationId);
    },
    [setConversationIdActions]
  );

  const resetConversationIdSelected = useCallback(() => {
    resetConversationId();
  }, [resetConversationId]);

  return {conversationId, setConversationId, resetConversationIdSelected};
}

export function useChatBotDetail(id?: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [chatBot, setChatBot] = useState<ChatBot | null>(null);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getChatbotByIdService(id)
      .then(res => {
        setChatBot(res);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const toggleChatBotEnabled = useCallback(
    (enabled: boolean) => {
      if (!chatBot) return;

      editChatbotService(chatBot.id ?? id, {enabled}).then(res => {
        setChatBot(res);
      });
    },
    [chatBot, id]
  );

  return {loading, error, chatBot, toggleChatBotEnabled};
}

export function useConversationChatbot(conversationId?: string) {
  const updateConversation = useActions(actions.updateStoreConversation);

  const toggleChatbotEnabled = async (disabled: boolean) => {
    if (!conversationId) return;
    return updateConversationChatbotService(conversationId, disabled).then(
      () => {
        updateConversation({
          id: conversationId,
          disabledChatbot: disabled,
        });
      }
    );
  };

  return {toggleChatbotEnabled};
}

export function useConversationActions(conversationId?: string) {
  const updateConversation = useActions(actions.updateStoreConversation);

  const togglePinConversation = async (pinned: boolean) => {
    if (!conversationId) return;

    return updateConversationService(conversationId, {pinned}).then(() => {
      updateConversation({
        id: conversationId,
        pinned,
      });
    });
  };

  const closeConversation = async () => {
    if (!conversationId) return;

    return updateConversationService(conversationId, {state: 'CLOSED'}).then(
      () => {
        updateConversation({
          id: conversationId,
          crmConversationState: 'CLOSED',
        });
      }
    );
  };

  return {togglePinConversation, closeConversation};
}

export default function useOnConversationChange<T: {id?: mixed}>(
  current: ?T,
  onChange: (prev: T, next: T) => void
): void {
  const prevRef = useRef<?T>();

  useEffect(() => {
    const prev = prevRef.current;
    if (prev != null && current != null && prev.id !== current.id) {
      onChange(prev, current);
    }
    prevRef.current = current;
  }, [current, onChange]);
}
